import React, { useEffect, useState } from "react";
import Step1 from "../components/Steps/welcom-page";
import Step3No from "../components/Steps/rejection-modal";
import GLP1Router from "../components/Steps/glp1-router";

// Shared core (Form A & B rejoin here after their own intro/pre-questions)
import AgeGate from "../components/Steps/age-gate";
import WeightLossReasons from "../components/Steps/weight-loss-reasons";
import Struggles from "../components/Steps/struggles";
import BmiCalculator from "../components/Steps/calculate";
import Gender from "../components/Steps/gender";
import Ethnicity from "../components/Steps/ethnicity";
import HealthConditions from "../components/Steps/heart-lung";
import Comorbidities from "../components/Steps/comorbidities";
import OtherConditions from "../components/Steps/other-conditions";
import MedicationUse from "../components/Steps/medication-use";
import OtherMedicationsFreetext from "../components/Steps/other-medications-freetext";
import AllergyCheck from "../components/Steps/allergy-check";
import MedicationTried from "../components/Steps/medication-tried";
import Glp1PriorWeight from "../components/Steps/glp1-prior-weight";
import Glp1CurrentDose from "../components/Steps/glp1-current-dose";
import Glp1LastDose from "../components/Steps/glp1-last-dose";
import Glp1SideEffects from "../components/Steps/glp1-side-effects";
import Glp1FollowupReason from "../components/Steps/glp1-followup-reason";
import PregnancyCheck from "../components/Steps/pregnancy-check";
import PregnancyNotice from "../components/Steps/pregnancy-notice";
import AdditionalInfo from "../components/Steps/additional-info";
import Acknowledgement from "../components/Steps/acknowledgement";
import ContactDetails from "../components/Steps/contact-details";
import TreatmentCheckout from "../components/Steps/treatment-checkout";

// Form B's own pre-questions
import CurrentMedicationDose from "../components/Steps/current-medication-dose";
import CurrentDose from "../components/Steps/current-dose";
import LastDoseDate from "../components/Steps/last-dose-date";
import DoseDuration from "../components/Steps/dose-duration";
import CurrentSideEffects from "../components/Steps/current-side-effects";
import CurrentSideEffectsModule from "../components/Steps/current-side-effects-module";
import TreatmentIntent from "../components/Steps/treatment-intent";
import TransferReason from "../components/Steps/transfer-reason";

// Form C placeholder
import FormCPlaceholder from "../components/Steps/form-c-placeholder";

// Matches the docs exactly: intro dialogue (welcome + name) straight into the router
// question. No awareness/"do you know Slimming Doctor" or comparison-table screens —
// those aren't in the new Form A/B specs.
const leadIn = [
  { id: 1, component: Step1 },
  { id: "router", component: GLP1Router },
];

// Shared by Form A and Form B — identical question set/order in both docs.
const coreSteps = [
  { id: "ageGate", component: AgeGate },
  { id: "reasons", component: WeightLossReasons },
  { id: "struggles", component: Struggles },
  { id: "bmi", component: BmiCalculator },
  { id: "gender", component: Gender },
  { id: "ethnicity", component: Ethnicity },
  { id: "8", component: HealthConditions },
  { id: "8f", component: Comorbidities },
  { id: "8d", component: OtherConditions },
  { id: "9", component: MedicationUse },
  { id: "9a", component: OtherMedicationsFreetext },
  { id: "10", component: AllergyCheck },
  { id: "13", component: MedicationTried },
  { id: "14", component: Glp1PriorWeight },
  { id: "15", component: Glp1CurrentDose },
  { id: "16", component: Glp1LastDose },
  { id: "17", component: Glp1SideEffects },
  { id: "18", component: Glp1FollowupReason },
  { id: "pregnancyGate", component: PregnancyCheck },
  { id: "pregnancyNotice", component: PregnancyNotice },
  { id: "additionalInfo", component: AdditionalInfo },
  { id: "acknowledgement", component: Acknowledgement },
  { id: "contact", component: ContactDetails },
  { id: "checkout", component: TreatmentCheckout },
];

const stepsFormA = [...leadIn, ...coreSteps];

const stepsFormB = [
  ...leadIn,
  { id: "b1", component: CurrentMedicationDose },
  { id: "b2", component: CurrentDose },
  { id: "b3", component: LastDoseDate },
  { id: "b4", component: DoseDuration },
  { id: "b5", component: CurrentSideEffects },
  { id: "b5a", component: CurrentSideEffectsModule },
  { id: "b6", component: TreatmentIntent },
  { id: "b7", component: TransferReason },
  ...coreSteps,
];

const stepsFormC = [...leadIn, { id: "formC", component: FormCPlaceholder }];

const FIRST_STEP_ID = { naive: "ageGate", current: "b1", previous: "formC" };

export default function MultiStepController() {
  // Detect a brand-new tab session and wipe any saved progress BEFORE the state
  // initializers below read localStorage. This MUST run first: the old reset lived in
  // an effect (which runs after state init), so it only cleared localStorage while the
  // in-memory formData kept the stale answers. That made the age gate — and every other
  // step that calls checkImmediateRed(formData) — reject the user based on leftover RED
  // answers (allergy/pregnancy/BMI/etc.) from a previous run, no matter what they picked.
  // Clearing up front guarantees formData truly starts empty on a new session.
  useState(() => {
    if (!sessionStorage.getItem("tab_active")) {
      localStorage.removeItem("currentStepIndex");
      localStorage.removeItem("formData");
      localStorage.removeItem("ageEligible");
      sessionStorage.setItem("tab_active", "true");
    }
    return true;
  });

  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    return Number(localStorage.getItem("currentStepIndex")) || 0;
  });

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem("formData");
    return saved ? JSON.parse(saved) : {};
  });

  const [showStep3NoModal, setShowStep3NoModal] = useState(false);

  const steps =
    formData.glp1History === "current" ? stepsFormB :
    formData.glp1History === "previous" ? stepsFormC :
    stepsFormA; // default before the router is answered, and for "naive"

  useEffect(() => {
    localStorage.setItem("formData", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem("currentStepIndex", currentStepIndex);
  }, [currentStepIndex]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStepIndex]);

  const goToStep = (stepId) => {
    const index = steps.findIndex((s) => s.id === stepId);
    if (index !== -1) setCurrentStepIndex(index);
  };

  // Called only by GLP1Router: switches the active step array (Form A/B/C) and jumps
  // to that form's first step in the same update, so the lookup always targets the
  // step array matching the NEW glp1History rather than the one still active this
  // render (see glp1-router.jsx for why a plain setFormData + goToStep pair can't do
  // this safely).
  const handleRouterSelect = (value) => {
    const targetSteps = value === "current" ? stepsFormB : value === "previous" ? stepsFormC : stepsFormA;
    const nextFormData = {
      ...formData,
      glp1History: value,
      isContinuingGlp1Treatment: value === "current",
    };
    setFormData(nextFormData);
    const index = targetSteps.findIndex((s) => s.id === FIRST_STEP_ID[value]);
    setCurrentStepIndex(index !== -1 ? index : 0);
  };

  const nextStep = () => {
    let nextIndex = currentStepIndex + 1;
    while (nextIndex < steps.length) {
      const step = steps[nextIndex];
      if (!step.condition || step.condition(formData)) break;
      nextIndex++;
    }
    if (nextIndex < steps.length) setCurrentStepIndex(nextIndex);
  };

  // The new-session detection/clearing now happens synchronously above (before state
  // init). Here we only need to mark the tab as closed on unload so the next load is
  // treated as a fresh session.
  useEffect(() => {
    const handleUnload = () => {
      sessionStorage.removeItem("tab_active");
    };

    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("unload", handleUnload);
    };
  }, []);

  const prevStep = () => {
    let prevIndex = currentStepIndex - 1;
    while (prevIndex >= 0) {
      const step = steps[prevIndex];
      if (!step.condition || step.condition(formData)) break;
      prevIndex--;
    }
    if (prevIndex >= 0) setCurrentStepIndex(prevIndex);
  };

  // Guards against stale currentStepIndex left in localStorage from before this
  // step-array restructure (a previously-saved index could point past the end of a
  // shorter/different array shape).
  const safeIndex = Math.min(Math.max(currentStepIndex, 0), steps.length - 1);
  const StepComponent = steps[safeIndex].component;

  return (
    <div>
      <StepComponent
        formData={formData}
        setFormData={setFormData}
        onNext={nextStep}
        onBack={prevStep}
        goToStep={goToStep}
        onRouterSelect={handleRouterSelect}
        showStep3No={() => setShowStep3NoModal(true)}
      />
      {showStep3NoModal && (
        <Step3No
          formData={formData}
          setFormData={setFormData}
          onBack={() => setShowStep3NoModal(false)}
          onClose={() => setShowStep3NoModal(false)}
        />
      )}
    </div>
  );
}
